import { Theme, Users } from "@just-in-types";
import * as React from "react";
import styled from "react-emotion";
import { connect } from "react-redux";
import Button from "../components/Button";
import Container from "../components/Container";
import NextButton from "../components/NextButton";
import Row from "../components/Row";
import Title from "../components/Title";
import VehiclesList from "../components/VehiclesList";
import Ymm from "../components/Ymm";
import makesJSON from "../data/makes";
import { firestore } from "../firebase";
import ErrorBoundary from "../hoc/withErrorBoundary";
import { RouteComponentProps } from "../Site";
import {
  addVehicle,
  AddVehicle,
  navigateToNext,
  NavigateToNext,
  removeVehicle,
  RemoveVehicle,
  reset,
  Reset,
} from "../store/actions";
import { IState } from "../store/reducers";
import { IStrings } from "../store/reducers/strings";

interface IVehiclesDispatchProps {
  onAddVehicle: AddVehicle;
  onNavigateToNext: NavigateToNext;
  onRemoveVehicle: RemoveVehicle;
  onReset: Reset;
}
interface IVehiclesStateProps {
  businessId: string;
  languageStrings: IStrings;
  rootPath: string;
  siteId: string;
  theme: Theme.Props;
  user: Users.User;
}
type VehiclesProps = RouteComponentProps & IVehiclesDispatchProps & IVehiclesStateProps;

interface IVehiclesState {
  dropdownsDisabled: boolean;
  make: null | string;
  makes: string[];
  model: null | string;
  models: string[];
  showVehicleAdder: boolean;
  vehiclePicks: null | Users.VehiclePick[];
  yearMax: null | number;
  yearMin: null | number;
  years: number[];
}

interface VehicleExtraState{
  modelYearRangeSwitch:boolean;
}
type VehiclesState = IVehiclesState & VehicleExtraState;

export type HandleSelect = (event: React.FormEvent<HTMLSelectElement>) => void;

const VehiclesContainer = styled(Container)`
  grid-template-rows: 10vh 60vh 15vh 15vh;
`;

const unFormat = (name: string) => name.replace(/_/g, " ").replace(/%/g, "/");

class Vehicles extends React.Component<VehiclesProps, VehiclesState> {
  public initialState: VehiclesState;

  private vehiclesRef = firestore.collection("vehicles");
  private startYear = 1960;
  private endYear = new Date().getFullYear();

  constructor(props: VehiclesProps) {
    super(props);

    const years: number[] = [];

    for (let i = this.startYear; i <= this.endYear; i++) {
      years.push(i);
    }

    this.initialState = {
      dropdownsDisabled: false,
      make: null,
      makes: [],
      model: null,
      modelYearRangeSwitch:false,
      models: [],
      showVehicleAdder: false,
      vehiclePicks: null,
      yearMax: null,
      yearMin: null,
      years,
    };

    this.state = this.initialState;
  }

  public toggleVehicleAdder = () => {
    this.setState({ showVehicleAdder: !this.state.showVehicleAdder });
    this.getVehiclePicks();
  };

  public handleSelect = (event: React.FormEvent<HTMLSelectElement>) => {
    let { yearMax, yearMin } = this.state;
    const { name, value } = event.currentTarget;

    if (name === "yearMin") {
      this.setState({ yearMin: parseInt(value, 10) });
    }
    if (yearMin && name === "yearMax") {
      this.setState({ yearMax: parseInt(value, 10) });
      this.getMakes(yearMin, parseInt(value, 10));
    }
    if (name === "make") {
      if (yearMin === null || yearMax === null) {
        yearMin = 1960;
        yearMax = new Date().getFullYear();
        this.setState({ yearMin, yearMax });
      }
      this.getModels(yearMin, yearMax, value);
    }
    if (name === "model") {
      this.setState({ model: value });
    }
  };

  public getMakes = async (yearMin: number | null | "none", yearMax: number | null | "none") => {
    console.log("getting makes...");
    console.log(yearMin + " " + yearMax);
    if (isNaN(yearMin as any)) {
      yearMin = 1960;
      if (yearMin === null) {
        this.setState({ yearMin: 1960 });
      }
    }
    if (isNaN(yearMax as any)) {
      yearMax = new Date().getFullYear();
      if (yearMax === null) {
        this.setState({ yearMax });
      }
    }
    this.setState({
      dropdownsDisabled: true,
      make: null,
      makes: [],
      model: null,
      models: [],
    });
    const unsorted: string[] = [];

    // @ts-ignore
    for (let year = yearMin; year <= yearMax; year++) {
      const makesByYear = makesJSON[year] as string[];

      if (makesByYear && Array.isArray(makesByYear)) {
        makesByYear.forEach((make) => {
          if (unsorted.indexOf(make) === -1) {
            unsorted.push(make);
          }
        });
      }
    }

    const makes = unsorted.sort((makeA, makeB) => (makeA === makeB ? 0 : makeA > makeB ? 1 : -1));

    this.setState({ dropdownsDisabled: false, makes });
  };

  public async componentDidMount() {
    const {
      onNavigateToNext,
      user: { userId },
    } = this.props;

    if (userId === "opt-out") {
      onNavigateToNext();
    }
    this.getMakes("none", "none");
    this.getVehiclePicks();
    await this.enableYearRange();
  }

  public getModels = async (yearMin: number, yearMax: number, make: string) => {
    this.setState({ dropdownsDisabled: true, model: null, models: [] });

    const query = await this.vehiclesRef
      .where("year", ">=", yearMin)
      .where("year", "<=", yearMax)
      .where("make", "==", make)
      .get();

    const models: string[] = [];

    query.forEach((doc) => {
      const data = doc.data();
      const { model } = data;

      if (model) {
        const unformatted = unFormat(model);

        if (models.indexOf(unformatted) === -1) {
          models.push(unformatted);
        }
      }
    });

    this.setState({ dropdownsDisabled: false, make, models });
  };

  public addVehicle = () => {
    const { businessId, languageStrings, onAddVehicle, siteId, user } = this.props;
    const { make, model, yearMax, yearMin } = this.state;

    const language = languageStrings.language === "esp" ? "esp" : "eng";

    if (make && model && yearMin && yearMax) {
      onAddVehicle(
        {
          businessId,
          language,
          make,
          model,
          siteId,
          yearMax,
          yearMin,
        } as Users.VehiclePick,
        user
      );
    }

    this.resetDropdowns();
    this.getVehiclePicks();
  };

  public removeVehicle = ({
    make,
    model,
    yearMax,
    yearMin,
  }: {
    make: string;
    model: string;
    yearMax: string;
    yearMin: string;
  }) => {
    const { onRemoveVehicle } = this.props;

    onRemoveVehicle({
      make,
      model,
      yearMax: parseInt(yearMax, 10),
      yearMin: parseInt(yearMin, 10),
    });

    this.resetDropdowns();
  };

  public handleEnter = () => this.props.onNavigateToNext();

  public resetDropdowns = () => this.setState(this.initialState);

  public getVehiclePicks = async () => {
    const {
      businessId,
      user: { mobile },
      siteId
    } = this.props;

    const tearApartVehicleSlc = (siteId && siteId === '17VBJyeH2crmqQC2OSxx' && businessId && businessId === 'gBHr1GdJSlpASgUvkoLW') ? true : false;
    const tearApartVehicleOgden = (siteId && siteId === '1YYyeZ1YXjGyc6Z6vwRt' && businessId && businessId === 'gBHr1GdJSlpASgUvkoLW') ? true : false;

    console.log("MOBILE: ");
    console.log(mobile);
    let query:any;
    if(tearApartVehicleSlc || tearApartVehicleOgden){
      query = await firestore
      .collection("vehiclePicks")
      .where("mobile", "==", mobile)
      .where("businessId", "==", businessId)
      .where("siteId", "==", siteId)
      .get();
    }else{
      query = await firestore
      .collection("vehiclePicks")
      .where("mobile", "==", mobile)
      .where("businessId", "==", businessId)
      .get();
    }

    const dataArr: Users.VehiclePick[] = [];
    query.forEach((doc) => {
      dataArr.push({ ...doc.data(), id: doc.ref.id } as Users.VehiclePick);
    });
    this.setState({ vehiclePicks: dataArr });
  };

  public removeVehiclePick = async (id: string) => {
    const { vehiclePicks } = this.state;
    firestore.collection("vehiclePicks").doc(id).delete();
    this.setState({ vehiclePicks: vehiclePicks.filter((v) => v.id !== id) });
  };

  public async enableYearRange(){
    const { businessId } = this.props;
    if(businessId){
      const business = await firestore
      .collection("businesses")
      .doc(businessId)
      .get();
      if (business.exists) {
        const businessData = business.data();
        if (businessData) {
          if(businessData.modelYearRangeSwitch){
            this.setState({ modelYearRangeSwitch : true });
          }else{
            this.setState({ modelYearRangeSwitch : false });
          }
        }
      }
  
    }
  }


  public render() {
    const {
      businessId,
      languageStrings: strings,
      theme: { colors },
      site: { kioskUiBackgroundColor, kioskUiTitleTextColor, kioskUiButtonErrorColor, kioskUiButtonSuccessColor },
      user: { mobile, userId },
    } = this.props;
    const site = this.props.site;
    const { dropdownsDisabled, make, makes, model, models, modelYearRangeSwitch, showVehicleAdder, yearMax, yearMin, vehiclePicks } =
      this.state;

    return (
      <ErrorBoundary>
        <VehiclesContainer style={{ background: kioskUiBackgroundColor }}>
          {userId !== "opt-out" && (
            <>
              <Row align="center" justify="center">
                <Title color={kioskUiTitleTextColor ? kioskUiTitleTextColor : colors.secondary} size="2rem">
                  {strings.vehiclesHeader}
                </Title>
              </Row>
              {showVehicleAdder ? (
                <Ymm
                  businessId={businessId}
                  colors={colors}
                  disabled={dropdownsDisabled}
                  make={make}
                  makes={makes}
                  models={models}
                  site={site}
                  onHandleSelect={this.handleSelect}
                  yearMax={yearMax}
                  yearMin={yearMin}
                  yearSwitch={modelYearRangeSwitch}
                />
              ) : (
                <VehiclesList colors={colors} removeVehiclePick={this.removeVehiclePick} vehicles={vehiclePicks} />
              )}
              {showVehicleAdder ? (
                <Row align="center" justify="center">
                  <Button
                    bg={kioskUiButtonErrorColor ? kioskUiButtonErrorColor : colors.error}
                    onClick={this.toggleVehicleAdder}
                  >
                    {strings.cancel}
                  </Button>
                  <Button
                    bg={kioskUiButtonSuccessColor ? kioskUiButtonSuccessColor : colors.success}
                    disabled={!model}
                    onClick={this.addVehicle}
                  >
                    {strings.add}
                  </Button>
                </Row>
              ) : (
                <Row align="center" justify="center">
                  <Button
                    bg={kioskUiButtonSuccessColor ? kioskUiButtonSuccessColor : colors.success}
                    onClick={this.toggleVehicleAdder}
                  >
                    {strings.addVehicle}
                  </Button>
                </Row>
              )}
              {showVehicleAdder ? (
                <div />
              ) : (
                <Row align="center" justify="center">
                  {/* <Button
                    bg={colors.primary}
                    disabled={showVehicleAdder}
                    onClick={() => window.history.go(-1)}
                  >
                    {strings.goBack}
                  </Button> */}
                  {/* <ResetButton /> */}
                  <NextButton
                    bg={kioskUiButtonSuccessColor ? kioskUiButtonSuccessColor : colors.success}
                    disabled={showVehicleAdder}
                    name="vehicles"
                    value={vehiclePicks}
                  >
                    {strings.next}
                  </NextButton>
                </Row>
              )}
            </>
          )}
        </VehiclesContainer>
      </ErrorBoundary>
    );
  }
}

const mapStateToProps = ({ router: { businessId, languageStrings, rootPath, siteId }, theme, user }: IState) => ({
  businessId,
  languageStrings,
  rootPath,
  siteId,
  theme,
  user,
});

const mapDispatchToProps = {
  onAddVehicle: addVehicle,
  onNavigateToNext: navigateToNext,
  onRemoveVehicle: removeVehicle,
  onReset: reset,
};

export default connect<IVehiclesStateProps, IVehiclesDispatchProps, RouteComponentProps>(
  // @ts-ignore
  mapStateToProps,
  // @ts-ignore
  mapDispatchToProps
)(Vehicles);



// WEBPACK FOOTER //
// ./src/views/Vehicles.tsx